class WishlistPage extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    this.checkConsent()
  }

  setupListeners () {
    super.setupListeners()

    this.setupWishlist = this.setupWishlist.bind(this)
    this.handleRemoveItem = this.handleRemoveItem.bind(this)
    this.wishlistError = this.wishlistError.bind(this)

    this.listeners.add(document, 'click', this.handleRemoveItem)

    window.SwymCallbacks = window.SwymCallbacks || []
    window.SwymCallbacks.push((swat) => {
      // Load all wishlists
      window._swat.fetchLists({
        callbackFn: (lists) => {
          if (lists.length > 0) {
            this.mergeAndLoadWishlists(lists)
          } else {
            // auto-create if none exists
            window._swat.createList(
              { lname: 'My Wishlist' },
              (newList) => this.loadWishlist(newList.lid),
              this.wishlistError,
            )
          }
        },
        errorFn: this.wishlistError,
      })
    })
  }

  checkConsent () {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) {
          console.error('Error loading features: ', error)
        }

        if (window.Shopify.customerPrivacy.userCanBeTracked() === false) {
          this.updateLoadingText()
        }
      },
    )
  }

  async mergeAndLoadWishlists (lists) {
    if (!lists || lists.length === 0) {
      this.log('[Wishlist] No lists to merge.')
      this.showEmptyPage()
      return
    }

    // Flatten items from all lists, keep reference to lid
    let mergedItems = lists.flatMap((list) =>
      (list.listcontents || []).map((item) => ({ ...item, lid: list.lid })),
    )

    // Deduplicate by variant ID
    const seen = new Set()
    mergedItems = mergedItems.filter((item) => {
      if (seen.has(item.epi)) return false
      seen.add(item.epi)
      return true
    })

    if (mergedItems.length === 0) {
      this.log('[Wishlist] No items found to merge.')
      this.showEmptyPage()
      return
    }

    this.log(
      '[Wishlist] Total merged items (deduplicated):',
      mergedItems.length,
    )

    const mergedWishlist = {
      list: {
        cnt: mergedItems.length,
        lid: lists[0].lid, // For share purposes, pick first list
      },
      items: mergedItems,
    }

    // Update share button with actual list ID
    if (this?.els?.share?.exists) {
      this.els.share.element.setAttribute(
        'data-wishlist-id',
        mergedWishlist.list.lid,
      )
      this.log(
        '[Wishlist] Share button list ID set to:',
        mergedWishlist.list.lid,
      )
    }

    await this.setupWishlist(mergedWishlist)
  }

  loadWishlist (lid) {
    window._swat.fetchListDetails(
      { lid },
      this.setupWishlist,
      this.wishlistError,
    )
  }

  async setupWishlist (wishlist) {
    // Render products
    const productCardHTML = await this.getAllProductCardHTML(wishlist)
    this.els.products.element.innerHTML = productCardHTML

    // Show either list panel or empty panel
    if (wishlist.list.cnt > 0) {
      this.els['panel-list'].element.setAttribute('aria-hidden', false)
      this.els['panel-empty'].element.setAttribute('aria-hidden', true)
    } else {
      this.els['panel-list'].element.setAttribute('aria-hidden', true)
      this.els['panel-empty'].element.setAttribute('aria-hidden', false)
    }

    // Show only the wishlist page
    this.els['page-loading'].element.setAttribute('aria-hidden', true)
    this.els['page-empty'].element.setAttribute('aria-hidden', true)
    this.els['page-wishlist'].element.setAttribute('aria-hidden', false)
  }

  async getAllProductCardHTML (wishlist) {
    const productCardHTML = await Promise.all(
      wishlist.items.map(
        async (product) => await this.getProductCardHTML(product),
      ),
    )
    return productCardHTML.filter(Boolean).join('')
  }

  async getProductCardHTML (product) {
    const productUrl = product.du.replace(
      '/products/',
      `${window.Shopify.routes.root}products/`,
    )

    return window.BAO.sectionRenderer
      .fetchSingle(
        'wishlist-product-card',
        `${productUrl}?variant=${product.epi}`,
      )
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
        const myself = newDOM.querySelector('.prd-Card')

        if (myself) {
          return `<div class="wsh-Page_Item">${myself.outerHTML.replace(
            'data-wishlist-page-el="remove"',
            `data-wishlist-page-el="remove" data-product-id="${product.empi}" data-variant-id="${product.epi}" data-product-url="${product.du}" data-wishlist-id="${product.lid}" data-product-title="${product.dt}" data-product-price="${product.pr * 100}"`,
          )}</div>`
        }
      })
      .catch((err) => {
        console.warn('[Wishlist] Failed to render product:', product.dt, err)
        return null
      })
  }

  handleRemoveItem (event) {
    const trigger = event.target.closest('[data-wishlist-page-el="remove"]')
    if (!trigger) return

    const product = {
      epi: parseInt(trigger.dataset.variantId, 10),
      empi: parseInt(trigger.dataset.productId, 10),
      du: trigger.dataset.productUrl,
    }

    const lid = trigger.dataset.wishlistId
    if (!lid) {
      console.warn('[Wishlist] No list ID found for product, cannot remove')
      return
    }

    window._swat.deleteFromList(
      lid,
      product,
      () => {
        this.pushRemoveFromWishlistEvent(trigger)

        window._swat.fetchLists({
          callbackFn: (lists) => this.mergeAndLoadWishlists(lists),
          errorFn: this.wishlistError,
        })
      },
      this.wishlistError,
    )
  }

  pushRemoveFromWishlistEvent (trigger) {
    window.dataLayer = window.dataLayer || []

    window.dataLayer.push({
      event: 'remove_from_wishlist',
      ecommerce: {
        currency: this.dataset.currency,
        value: parseInt(trigger.dataset.productPrice, 10) / 100,
        items: [
          {
            item_id: trigger.dataset.productId,
            item_name: trigger.dataset.productTitle,
          },
        ],
      },
    })
  }

  wishlistError (errorMessage) {
    this.log(`Wishlist error: ${errorMessage}`)
    this.showEmptyPage()
  }

  showEmptyPage () {
    this.els['page-loading'].element.setAttribute('aria-hidden', true)
    this.els['page-empty'].element.setAttribute('aria-hidden', false)
    this.els['page-wishlist'].element.setAttribute('aria-hidden', true)
  }

  updateLoadingText () {
    this.els['loading-text'].element.innerHTML = `
      ${window.theme.strings.wishlist.cookieMessage}
      <button type="button" onclick="privacyBanner.showPreferences();" class="wsh-Page_Link">${window.theme.strings.wishlist.openCookieBanner}</button>
    `
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage)
}
